import { Link } from 'react-router-dom';
import { ArrowRight, LoaderCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { DatasetActionsMenu } from './DatasetActionsMenu';
import type { DatasetSummaryDto } from '@/api/types';
import styles from './DatasetRow.module.css';
import { routes } from '@/lib/routePaths';

interface DatasetRowProps {
  dataset: DatasetSummaryDto;
  onEdit: () => void;
  onDelete: () => void;
  busy: boolean;
}

export function DatasetRow({ dataset, onEdit, onDelete, busy }: DatasetRowProps) {
  return (
    <article className={styles.row} aria-busy={busy}>
      <div className={styles.content}>
        <div className={styles.summary}>
          <div className={styles.titleLine}>
            <Link className={styles.title} to={dataset.id ? routes.dataset(dataset.id) : routes.datasets}>{dataset.displayName}</Link>
            <span className={styles.instrument}>{dataset.instrument}</span>
            <Badge variant={dataset.status === 'Failed' ? 'danger' : 'success'}>{dataset.status}</Badge>
          </div>
          <div className={styles.meta}>
            <span>{formatRange(dataset)}</span>
            <span>{(dataset.candleCount ?? 0).toLocaleString()} candles</span>
            <span>{(dataset.newsEventCount ?? 0).toLocaleString()} news</span>
            <span>Imported {dataset.createdUtc ? new Date(dataset.createdUtc).toLocaleString() : 'N/A'}</span>
          </div>
        </div>
        <div className={styles.actions}>
          {busy ? (
            <span className={styles.busy}><LoaderCircle size={15} /> Processing</span>
          ) : (
            <Link to={dataset.id ? routes.dataset(dataset.id) : routes.datasets}>
              <Button variant="primary">
                Open dataset <ArrowRight size={15} />
              </Button>
            </Link>
          )}
          <DatasetActionsMenu
            dataset={dataset}
            onEdit={onEdit}
            onDelete={onDelete}
            disabled={busy}
          />
        </div>
      </div>
      {(dataset.importErrors?.length ?? 0) > 0 && (
        <p className={styles.errors}>{(dataset.importErrors ?? []).join('; ')}</p>
      )}
    </article>
  );
}

function formatRange(dataset: DatasetSummaryDto) {
  const from = dataset.fromUtc ? new Date(dataset.fromUtc).toLocaleDateString() : 'N/A';
  const to = dataset.toUtc ? new Date(dataset.toUtc).toLocaleDateString() : 'N/A';
  return `${from} - ${to}`;
}
